// Training's localStorage, tolerant of a browser that refuses it (a private
// window, a full quota, storage blocked outright). Every access is in a
// try/catch, as in hooks/useStoredChoice.js; a write the browser refuses
// is kept in memory instead, so the page goes on working for this visit
// and says so (each write reports whether it reached storage).
//
// Keys are per person — `mono.training.<userId>.…` — because logout keeps
// the set queue (unsent sets must survive until they're sent, D14; only the
// plan drafts go, draft.js clearAllDrafts): an unkeyed queue would flush
// into the next account signed in on the same phone.

// Values whose last write storage refused, by key. A later successful
// write drops the entry, so storage is authoritative again.
const memory = new Map();

/** `mono.training.<userId>.<part>.<part>…` */
export function trainingKey(userId, ...parts) {
  if (userId === null || userId === undefined || userId === "") {
    throw new TypeError("Training storage needs the signed-in person's id.");
  }
  return ["mono.training", userId, ...parts].join(".");
}

/** The stored value (parsed), or undefined when nothing (or junk) is there. */
export function readStored(key) {
  if (memory.has(key)) return memory.get(key);
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined; // blocked storage, or junk
  }
}

/**
 * Store `value` (null or undefined removes the key). Returns true when it
 * reached localStorage, false when it is only in memory for this visit.
 */
export function writeStored(key, value) {
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
    memory.delete(key);
    return true;
  } catch {
    memory.set(key, value ?? null);
    return false;
  }
}

/** Every key that starts with `prefix`, in storage or held in memory. */
export function storedKeys(prefix) {
  const keys = new Set();
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(prefix)) keys.add(key);
    }
  } catch {
    // blocked storage: only what memory holds
  }
  for (const key of memory.keys()) if (key.startsWith(prefix)) keys.add(key);
  return [...keys];
}

/** Tests only: forget what refused writes left in memory. */
export function resetMemoryForTests() {
  memory.clear();
}
